import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getVideos, saveVideo, deleteVideo } from '../services/videoService';
import VideoCategoryInput from '../components/VideoCategoryInput';

const VideosPage = () => {
  const navigate = useNavigate();
  const { isAuthorized } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingVideo, setIsAddingVideo] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    link: '',
    category: ''
  });

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const videoList = await getVideos();
      setVideos(videoList);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.date || !formData.link) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await saveVideo(formData);
      setFormData({ title: '', date: '', link: '', category: '' });
      setIsAddingVideo(false);
      loadVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Error saving video. Please try again.');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await deleteVideo(videoId);
        loadVideos();
      } catch (error) {
        console.error('Error deleting video:', error);
        alert('Error deleting video. Please try again.');
      }
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const formatDate = (dateString) => {
    // Create date object and add timezone offset to avoid day shifting
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-12 text-text-dark text-lg">Loading videos...</div>
      </div>
    );
  }

  return (
    <div className="w-full p-8">
      <header className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 text-text-dark hover:text-accent cursor-pointer bg-transparent border-none transition-colors duration-200"
            title="Back to Home"
          >
            <span className="material-icons text-2xl">arrow_back</span>
          </button>
          <div className="flex items-center gap-3">
            <img src="/bear.png" alt="Logo" className="w-12 h-12 object-contain" />
            <h1 className="m-0 text-text-dark text-3xl font-light font-serif">Game Videos</h1>
          </div>
        </div>
        {isAuthorized && (
          <button
            className="p-3 sm:px-6 sm:py-3 bg-accent text-text-light border-none rounded text-base cursor-pointer transition-colors duration-200 flex items-center justify-center sm:justify-start sm:gap-2 hover:bg-accent/80"
            onClick={() => setIsAddingVideo(!isAddingVideo)}
          >
            <span className="material-icons">{isAddingVideo ? 'close' : 'add'}</span>
            <span className="hidden sm:inline">{isAddingVideo ? 'Cancel' : 'Add Video'}</span>
          </button>
        )}
      </header>

      {isAddingVideo && (
        <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-text-dark font-serif mb-4">Add New Video</h2>
          <form onSubmit={handleAddVideo} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-text-dark mb-1">
                Title
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Enter video title"
                required
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-text-dark mb-1">
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
            </div>
            <div>
              <label htmlFor="link" className="block text-sm font-medium text-text-dark mb-1">
                YouTube Link
              </label>
              <input
                type="url"
                id="link"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
            </div>
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-text-dark mb-1">
                Category
              </label>
              <VideoCategoryInput
                value={formData.category}
                onChange={handleInputChange}
                name="category"
              />
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-3 bg-accent text-text-light border-none rounded cursor-pointer transition-colors duration-200 hover:bg-accent/80"
              >
                Save Video
              </button>
              <button
                type="button"
                onClick={() => setIsAddingVideo(false)}
                className="px-6 py-3 bg-gray-500 text-white border-none rounded cursor-pointer transition-colors duration-200 hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {videos.length === 0 ? (
        <div className="text-center py-12 text-text-dark">
          <p className="text-lg m-0 font-sans">No videos yet. Add your first video to get started!</p>
        </div>
      ) : (
        (() => {
          // Group videos by category and sort by date descending
          const groupedVideos = videos.reduce((groups, video) => {
            const category = video.category || 'Uncategorized';
            if (!groups[category]) {
              groups[category] = [];
            }
            groups[category].push(video);
            return groups;
          }, {});

          // Sort videos within each category by date descending
          Object.keys(groupedVideos).forEach(category => {
            groupedVideos[category].sort((a, b) => new Date(b.date) - new Date(a.date));
          });

          // Sort categories by the youngest (most recent) video date in each category, descending
          const sortedCategories = Object.keys(groupedVideos).sort((a, b) => {
            const aNewestDate = new Date(Math.max(...groupedVideos[a].map(video => new Date(video.date))));
            const bNewestDate = new Date(Math.max(...groupedVideos[b].map(video => new Date(video.date))));
            return bNewestDate - aNewestDate; // Descending order (newest categories first)
          });

          return (
            <div className="space-y-12">
              {sortedCategories.map(category => (
                <div key={category}>
                  <h2 className="text-2xl font-semibold text-text-dark font-serif mb-6 pb-2 border-b border-gray-200">
                    {category}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {groupedVideos[category].map((video) => {
                      const embedUrl = getYouTubeEmbedUrl(video.link);
                      return (
                        <div key={video.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                          {embedUrl && (
                            <div className="aspect-video">
                              <iframe
                                src={embedUrl}
                                title={video.title}
                                className="w-full h-full"
                                frameBorder="0"
                                allowFullScreen
                              />
                            </div>
                          )}
                          <div className="p-4">
                            <h3 className="text-lg font-semibold text-text-dark font-serif mb-2">{video.title}</h3>
                            <p className="text-sm text-gray-600 mb-2">{formatDate(video.date)}</p>
                            <div className="flex justify-between items-center">
                              <a
                                href={video.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent hover:text-accent/80 text-sm font-medium"
                              >
                                Watch on YouTube
                              </a>
                              {isAuthorized && (
                                <button
                                  onClick={() => handleDeleteVideo(video.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Delete video"
                                >
                                  <span className="material-icons text-lg">delete</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </div>
  );
};

export default VideosPage;